/**
 * Station Login Screen
 *
 * Full-screen PIN pad shown when iPad app opens/wakes.
 * Barber enters their 4-digit PIN to identify themselves.
 * System looks up which barber has that PIN.
 *
 * This is DIFFERENT from:
 * - LoginPage: Firebase account login (shop admin)
 * - LockScreen: Inactivity lock (knows who the barber is)
 * - BarberQuickSelect: List-based selection (manual pick)
 *
 * This is PIN-BASED IDENTIFICATION:
 * - Barber enters PIN → System finds matching barber → Station is now "theirs"
 */

import React, { useState, useEffect } from 'react';
import { Scissors, Delete, AlertCircle, Check } from 'lucide-react';
import { barberDataService, Barber } from '../../services/barberDataService';
import { verifyBarberPin, BarberPinAuthError } from '../../services/barberAuthService';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface StationLoginScreenProps {
  onLogin: (barber: Barber) => void;
  shopName?: string;
}

const StationLoginScreen: React.FC<StationLoginScreenProps> = ({
  onLogin,
  shopName = 'SLIK Barber Lounge'
}) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [barbers, setBarbers] = useState<Barber[]>([]);
  // "Keep me on this device" (Jose 2026-10-21): checked BEFORE entering the PIN → once verified, the
  // operator stays signed in (no PIN pad on wake). Honored ONLY for owner/manager (a shared-station
  // barber can't accidentally leave their session open). Mirrors the barber-profile Stay-signed-in toggle.
  const [stayOn, setStayOn] = useState(false);
  const { logout } = useAuth(); // escape hatch: fully sign out the shop account → /login

  // Load barbers on mount (for PIN matching).
  useEffect(() => {
    const load = async () => {
      try {
        const allBarbers = await barberDataService.getAllBarbersAsync();
        setBarbers(allBarbers);
      } catch (err) {
        console.error('Error loading barbers:', err);
      }
    };
    load();
  }, []);

  // Clear error after 3 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleNumberClick = (num: string) => {
    if (pin.length < 4) {
      const newPin = pin + num;
      setPin(newPin);
      setError('');

      // Auto-submit when 4 digits entered
      if (newPin.length === 4) {
        setTimeout(() => handleSubmit(newPin), 200);
      }
    }
  };

  const handleDelete = () => {
    setPin(pin.slice(0, -1));
    setError('');
  };

  const handleClear = () => {
    setPin('');
    setError('');
  };

  const handleSubmit = async (pinToCheck?: string) => {
    const pinValue = pinToCheck || pin;

    if (pinValue.length !== 4) {
      setError('Please enter your 4-digit PIN');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Server-side PIN verification (BEHAVIOR-PIN: barbers-PIN security refactor 2026-09-29). The raw
      // PIN no longer lives on the barbers doc — barberSignIn checks the hashed secret + rate-limits.
      const result = await verifyBarberPin(pinValue, stayOn);

      // Resolve the full Barber object for the app (barbers no longer carry pin — look up by id).
      let matchingBarber = barbers.find(b => b.id === result.barberId)
        || barberDataService.getBarberById(result.barberId)
        || undefined;

      // HONOR THE CHECKBOX (Jose 2026-10-21): "keep me signed in on this device" is an explicit per-device
      // choice — no role gate. (The earlier owner/manager gate silently no-op'd because the role wasn't
      // resolved at that instant, so the box "didn't stick".) Only the in-app Lock Screen button locks it.
      if (matchingBarber) {
        console.log('[Station Login] Barber identified:', matchingBarber.firstName, matchingBarber.lastName);
        toast.success(`Welcome, ${matchingBarber.firstName}!`);
        onLogin({ ...matchingBarber, staySignedIn: stayOn } as Barber);
      } else {
        // Verified server-side but no local record loaded — proceed with a minimal identity.
        const [first, ...rest] = (result.name || 'Barber').split(' ');
        toast.success(`Welcome, ${first}!`);
        onLogin({ id: result.barberId, firstName: first, lastName: rest.join(' '), role: result.role, staySignedIn: stayOn } as any as Barber);
      }
    } catch (err: any) {
      const msg = err instanceof BarberPinAuthError ? err.message : 'Something went wrong. Please try again.';
      if (!(err instanceof BarberPinAuthError)) console.error('[Station Login] Error:', err);
      setError(msg);
      setPin('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    } else if (e.key === 'Backspace') {
      handleDelete();
    } else if (e.key === 'Escape') {
      handleClear();
    } else if (/^[0-9]$/.test(e.key)) {
      handleNumberClick(e.key);
    }
  };

  const numberButtons = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', ''];

  return (
    <div
      className="fixed inset-0 z-[9999] bg-gradient-to-br from-purple-900 via-indigo-900 to-indigo-900 touch-manipulation"
      onKeyDown={handleKeyPress}
      tabIndex={-1}
    >
      {/* Station Login Content — Mobile/iPad First. SCROLLABLE (Jose 2026-10-23): this was `h-full …
          justify-center` on a fixed overlay, so on a shorter iPad the content (logo + name picker + PIN
          + "Keep me signed in") overflowed and got CENTER-CLIPPED — the bottom toggle was unreachable
          ("couldn't reach Stay-signed-in, looked zoomed in"). `overflow-y-auto` + `min-h-full` centers
          when it fits and SCROLLS when it doesn't, so every control is always reachable. */}
      <div className="absolute inset-0 overflow-y-auto overscroll-contain">
        <div className="min-h-full flex flex-col items-center justify-center p-4 md:p-8 py-8 safe-area-inset">
        {/* Logo — the SLIK emblem (Jose 2026-09-21: the PIN pad was showing a generic scissors
            icon instead of the shop logo). Falls back to the scissors mark if the image fails. */}
        <div className="mb-4 md:mb-6">
          <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-white/15 flex items-center justify-center overflow-hidden">
            <img
              src={`${process.env.PUBLIC_URL || ''}/assets/branding/beard-mark-white.png`}
              alt={shopName}
              className="w-full h-full object-contain p-2"
              onError={(e) => {
                const img = e.currentTarget;
                img.style.display = 'none';
                const fb = img.nextElementSibling as HTMLElement | null;
                if (fb) fb.style.display = 'block';
              }}
            />
            <Scissors className="h-10 w-10 md:h-14 md:w-14 text-white" style={{ display: 'none' }} />
          </div>
        </div>

        {/* Shop Name */}
        <h1 className="text-2xl md:text-4xl font-bold text-white mb-1 md:mb-2 text-center">{shopName}</h1>
        <p className="text-lg md:text-xl text-white/80 mb-6 md:mb-8">Enter your PIN to sign in</p>

        {/* PIN Display - 4 large dots for visibility */}
        <div className="mb-6 md:mb-8 flex items-center justify-center space-x-5 md:space-x-6">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-6 h-6 md:w-7 md:h-7 rounded-full border-3 transition-all duration-200 ${
                i < pin.length
                  ? 'bg-white border-white scale-125'
                  : 'bg-transparent border-white/40'
              }`}
            />
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 md:mb-6 px-5 py-3 bg-red-500/20 border border-red-500/50 rounded-xl flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-300 flex-shrink-0" />
            <p className="text-red-200 font-medium text-sm md:text-base">{error}</p>
          </div>
        )}

        {/* Number Keypad - LARGE touch targets for iPad */}
        <div className="grid grid-cols-3 gap-3 md:gap-5 mb-4 md:mb-6">
          {numberButtons.map((num, index) => (
            num ? (
              <button
                key={index}
                onClick={() => handleNumberClick(num)}
                disabled={isLoading || pin.length >= 4}
                className="w-[72px] h-[72px] md:w-24 md:h-24 rounded-2xl bg-white/15 hover:bg-white/20 active:bg-white/30
                         border border-white/20 text-white text-3xl md:text-4xl font-semibold
                         transition-colors duration-100 disabled:opacity-50 disabled:cursor-not-allowed
                         shadow-lg active:scale-95 touch-manipulation select-none"
              >
                {num}
              </button>
            ) : (
              <div key={index} className="w-[72px] h-[72px] md:w-24 md:h-24" />
            )
          ))}
        </div>

        {/* Action Buttons - Larger for touch */}
        <div className="flex items-center space-x-3 md:space-x-4">
          <button
            onClick={handleDelete}
            disabled={isLoading || pin.length === 0}
            className="px-5 py-3 md:px-8 md:py-4 rounded-xl bg-white/15 hover:bg-white/20 active:bg-white/30
                     border border-white/20 text-white font-medium text-base md:text-lg
                     transition-colors duration-100 disabled:opacity-50 disabled:cursor-not-allowed
                     flex items-center space-x-2 touch-manipulation active:scale-95"
          >
            <Delete className="h-5 w-5 md:h-6 md:w-6" />
            <span>Delete</span>
          </button>

          <button
            onClick={handleClear}
            disabled={isLoading || pin.length === 0}
            className="px-5 py-3 md:px-8 md:py-4 rounded-xl bg-white/15 hover:bg-white/20 active:bg-white/30
                     border border-white/20 text-white font-medium text-base md:text-lg
                     transition-colors duration-100 disabled:opacity-50 disabled:cursor-not-allowed
                     touch-manipulation active:scale-95"
          >
            Clear
          </button>
        </div>

        {/* ⭐ Stay-signed-in ON THE PAD (Jose 2026-10-21): check it, enter your PIN once, and the pad won't
            come back on wake — you stay on your profile. Owner/manager only (ignored for a shared-station
            barber). This is the "button on top so the PIN doesn't keep coming up". */}
        <button
          type="button"
          onClick={() => setStayOn(v => !v)}
          className={`mt-5 w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl border transition-colors touch-manipulation ${stayOn ? 'bg-emerald-500/25 border-emerald-400/60 text-emerald-100' : 'bg-white/10 border-white/20 text-white/80'}`}
        >
          <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${stayOn ? 'bg-emerald-500 border-emerald-400' : 'border-white/40'}`}>
            {stayOn && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
          </span>
          <span className="text-sm md:text-base font-semibold">Keep me signed in on this device</span>
        </button>

        {/* PIN pad kept minimal (Jose 2026-09-27): removed the "start your shift" line, the barber-PIN
            count, the device-station dropdown (station assignment lives in Settings → Stations, done in
            back), and the email-login BYPASS (owners PIN in — their PIN resolves to owner; the bypass was
            a barber→login/create-account path). */}

        {/* Escape hatch (Jose 2026-10-17): if the PIN won't work or this device needs to sign into a
            different shop account, this fully signs OUT the shop account and lands on /login. NOT the old
            create-account bypass — it's a logout → the owner can log back in and re-PIN. Un-traps a device
            stuck on a broken PIN with no reachable sidebar. */}
        <button
          onClick={() => { if (window.confirm('Sign out of this device and go to the login screen?')) logout(); }}
          className="mt-6 text-sm md:text-base text-white/50 hover:text-white/90 underline underline-offset-4 transition-colors touch-manipulation"
        >
          Sign in to a different account
        </button>
        </div>
      </div>
    </div>
  );
};

export default StationLoginScreen;
